import { Button } from "@/app/common/common-components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/app/common/common-components/ui/DropdownMenu";
import { ChevronDown, Mail, MessageCircle, RefreshCw } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/common/common-components/ui/Table";
import { WhatsAppIcon } from "@/app/common/common-components/BrandIcons";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/app/common/common-components/ui/HoverCard";
import PreviewMessageDialog from "@/features/contact/SaveContactTabs/MessageTab/PreviewMessageDialog";
import HelpLinkIcon from "@/app/common/common-components/HelpLinkIcon";
import { useState } from "react";
import {
  getMessageType,
  stripHtmlTags,
} from "@/app/common/common-functions/functions";
import { useStore } from "@/app/stores/store";
import apiAgent from "@/app/api/apiAgent";
import { useQuery } from "@tanstack/react-query";
import { ContactMessageModel } from "@/app/models/ContactMessageModel";
import { ContactModel } from "@/app/models/ContactModel";
import NewMessageDialog from "@/features/contact/SaveContactTabs/MessageTab/NewMessageDialog";
import NewMessageButton from "@/features/contact/SaveContactTabs/MessageTab/NewMessageButton";

const MessageTab = ({ contact }: { contact: ContactModel }) => {
  const [selectedMessage, setSelectedMessage] =
    useState<ContactMessageModel | null>(null);
  const [initialMessage, setInitialMessage] = useState<{
    type: "Whatsapp" | "SMS" | "Email";
    date: string;

    message: string;
    subject?: string;
  } | null>(null);
  const { userStore } = useStore();

  const { userSession } = userStore;

  const {
    data: messages,
    isLoading: isLoadingMessages,
    error: messagesError,
  } = useQuery({
    queryKey: ["contactMessages", contact.contactID],
    queryFn: () => apiAgent.Contact.getContactMessages(contact.contactID),
    enabled: !!contact.contactID,
    retry: 2,
  });

  const contactDetails = JSON.parse(contact.contact);

  const contactPhone = contactDetails.phone_dialing_code + contactDetails.phone;

  const variables = [
    {
      label: "Contact First Name",
      value: contact.firstName,
      replace: "{{contact_first_name}}",
    },
    {
      label: "Contact Last Name",
      value: contact.lastName,
      replace: "{{contact_last_name}}",
    },
    {
      label: "Contact Email",
      value: contact.emailAddress,
      replace: "{{contact_email}}",
    },
    {
      label: "Contact Phone",
      value: contactPhone,
      replace: "{{contact_phone}}",
    },
    {
      label: "Agent First Name",
      value: userSession!.displayName,
      replace: "{{agent_first_name}}",
    },
    {
      label: "Agent Email",
      value: userSession!.emailAddress,
      replace: "{{agent_email}}",
    },
  ];

  const handleNewMessageClick = (messageType: "Whatsapp" | "SMS" | "Email") => {
    setInitialMessage({
      type: messageType,
      date: new Date().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      }),
      message: "",
    });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-items-center tw-justify-between">
        <NewMessageButton handleNewMessageClick={handleNewMessageClick} />
      </div>
      <div className="tw-max-h-[600px] tw-overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead></TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingMessages ? (
              <TableRow>
                <TableCell colSpan={3} className="tw-py-8 tw-text-center">
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                    <div className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-b-2 tw-border-primary"></div>
                    Loading messages...
                  </div>
                </TableCell>
              </TableRow>
            ) : messagesError ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="tw-py-8 tw-text-center tw-text-red-500"
                >
                  Error loading messages. Please try again.
                </TableCell>
              </TableRow>
            ) : messages && messages.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="tw-py-8 tw-text-center tw-text-muted-foreground"
                >
                  No messages found
                </TableCell>
              </TableRow>
            ) : (
              messages &&
              messages.map((message, index) => (
                <TableRow
                  key={index}
                  className="hover:tw-bg-muted/50 tw-cursor-pointer"
                  onClick={() => setSelectedMessage(message)}
                >
                  <TableCell className="tw-w-0">
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <span>
                          {getMessageType(message.type) === "Whatsapp" && (
                            <WhatsAppIcon className="tw-size-4 tw-fill-primary" />
                          )}
                          {getMessageType(message.type) === "SMS" && (
                            <MessageCircle className="tw-size-4 tw-text-primary" />
                          )}
                          {getMessageType(message.type) === "Email" && (
                            <Mail className="tw-size-4 tw-text-primary" />
                          )}
                        </span>
                      </HoverCardTrigger>
                      <HoverCardContent className="tw-w-fit tw-px-2 tw-py-1 tw-text-xs">
                        {getMessageType(message.type)}
                      </HoverCardContent>
                    </HoverCard>
                  </TableCell>
                  <TableCell className="tw-w-0 tw-truncate">
                    {message.dateOn}
                  </TableCell>
                  <TableCell className="tw-max-w-0 tw-truncate">
                    {getMessageType(message.type) === "Email"
                      ? stripHtmlTags(message.message)
                      : message.message}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PreviewMessageDialog
        message={selectedMessage}
        open={!!selectedMessage}
        onOpenChange={(open) => !open && setSelectedMessage(null)}
      />
      <NewMessageDialog
        open={!!initialMessage}
        onOpenChange={(open) => !open && setInitialMessage(null)}
        contact={contact}
        initialMessage={initialMessage}
        variables={variables}
        userId={userSession!.userId}
      />
    </div>
  );
};

export default MessageTab;
